// ============================================
// AUTH STORE
// ============================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  role: 'SEAFARER';
  firstName?: string;
  lastName?: string;
}

interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  
  // Actions
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  updateUser: (data: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      accessToken: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user }),

      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),

      login: (user, accessToken, refreshToken) => {
        const domain = window.location.hostname.includes('crewatsea.com') ? 'domain=.crewatsea.com;' : '';
        document.cookie = `crewatsea_role=${user.role}; ${domain} path=/; max-age=31536000`;
        set({
          user,
          accessToken,
          refreshToken,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        const domain = window.location.hostname.includes('crewatsea.com') ? 'domain=.crewatsea.com;' : '';
        document.cookie = `crewatsea_role=; ${domain} path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },

      setLoading: (isLoading) => set({ isLoading }),

      updateUser: (data) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...data } : null,
        })),
    }),
    {
      name: 'seafarer-auth-storage',
      partialize: (state) => ({
        user: state.user ? {
          id: state.user.id,
          email: state.user.email,
          role: state.user.role,
          firstName: state.user.firstName,
          lastName: state.user.lastName,
        } : null,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
